/**
 * Parsing and interpretation of the ICE candidates a WebRTC connection gathers.
 *
 * Only server-reflexive ("srflx") candidates say anything about the firewall: they are the
 * address a STUN server saw the packet arrive from, so getting one at all proves UDP reached
 * that server and came back.
 */

/** The public address a STUN server reported back for this browser. */
export interface SrflxCandidate {
	ip: string
	port: number
}

/**
 * A NAT that maps the same internal socket to one external port regardless of destination
 * ("cone") works with IP telephony. One that picks a new port per destination ("symmetric")
 * breaks the media path the SIP signalling agreed on, which is heard as one-way or missing audio.
 */
export type NatType = 'cone' | 'symmetric'

/**
 * Reads the mapped address out of a candidate line, or returns null if the line is not a usable
 * server-reflexive candidate. Layout of the fields is fixed by RFC 5245:
 * `candidate:<foundation> <component> <transport> <priority> <ip> <port> typ <type> ...`
 */
export function parseSrflxCandidate(candidate: string): SrflxCandidate | null {
	const line = candidate.trim().replace(/^a=/, '')
	if (!line.includes('typ srflx')) return null

	const fields = line.split(/\s+/)
	const ip = fields[4]
	const port = Number(fields[5])

	// mDNS candidates hide the local address behind a random *.local name and carry no public IP.
	if (!ip || ip.endsWith('.local')) return null
	if (!Number.isInteger(port) || port <= 0 || port > 65535) return null

	return { ip, port }
}

/** The distinct external ports seen across every candidate, in the order they arrived. */
export function uniquePorts(candidates: SrflxCandidate[]): number[] {
	return [...new Set(candidates.map((candidate) => candidate.port))]
}

/**
 * More than one external port across the STUN servers we asked means the NAT re-maps per
 * destination.
 */
export function classifyNat(ports: number[]): NatType {
	return new Set(ports).size > 1 ? 'symmetric' : 'cone'
}
